"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordsExportSchema = exports.recordsAccessRequestSchema = exports.activitySchema = exports.resolvedActivityEventSchema = exports.activityEventSchema = exports.documentContentUrlSchema = exports.documentDetailSchema = exports.documentListSchema = exports.documentSchema = exports.documentVersionSchema = exports.documentRequestListSchema = exports.createDocumentRequestSchema = exports.documentRequestSchema = exports.requestStateSchema = exports.exchangeListSchema = exports.updateExchangeRequestSchema = exports.createExchangeRequestSchema = exports.exchangeSchema = exports.exchangeStateSchema = exports.associateGenericUploadsResponseSchema = exports.associateGenericUploadsRequestSchema = exports.genericLinkUploadsResponseSchema = exports.genericLinkUploadItemSchema = exports.uploadLinkResponseSchema = exports.createUploadLinkRequestSchema = exports.uploadLinkSchema = exports.uploadLinkStatusSchema = exports.contactListSchema = exports.reconcileContactResponseSchema = exports.reconcileActionSchema = exports.reconcileContactRequestSchema = exports.updateContactRequestSchema = exports.createContactRequestSchema = exports.contactSchema = exports.contactSourceSchema = exports.contactKindSchema = void 0;
/**
 * Wire types for `/api/v1`, the rep and client surface (section 13).
 *
 * Imports nothing internal: Zod and TypeScript only, enforced by the zone rule. These are
 * the shapes the frontends parse, so every field here is one the surface may rely on, and
 * anything absent is deliberately not promised.
 *
 * Two conventions worth knowing. Request schemas are `.strict()`, so a client sending a
 * field the endpoint does not define is told rather than having it silently dropped -
 * which is how a rep discovers their title was never saved. And nothing here carries a
 * `version` field: the validator travels as an `ETag` header and an `If-Match`, because
 * putting it in the body as well would give a client two places to read it from and one
 * of them would eventually be wrong.
 */
const zod_1 = require("zod");
const uuid = zod_1.z.uuid();
// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------
exports.contactKindSchema = zod_1.z.enum(['prospect', 'client']);
/** How the contact came to exist. Only generic_link and portal_and_generic_link are tracked; rep/crm contacts are null. */
exports.contactSourceSchema = zod_1.z.enum(['generic_link', 'portal_and_generic_link']);
exports.contactSchema = zod_1.z.object({
    id: uuid,
    kind: exports.contactKindSchema,
    email: zod_1.z.string(),
    displayName: zod_1.z.string(),
    externalRef: zod_1.z.string().nullable(),
    /** Present so a surface can show "removed", not so it can filter: reads exclude them. */
    removedAt: zod_1.z.iso.datetime().nullable(),
    source: exports.contactSourceSchema.nullable(),
});
exports.createContactRequestSchema = zod_1.z
    .object({
    kind: exports.contactKindSchema,
    email: zod_1.z.string().min(3).max(254),
    displayName: zod_1.z.string().min(1).max(200),
})
    .strict();
exports.updateContactRequestSchema = zod_1.z
    .object({
    email: zod_1.z.string().min(3).max(254).optional(),
    displayName: zod_1.z.string().min(1).max(200).optional(),
    /**
     * The CRM's own identifier for this person, claimed once (DG.26).
     *
     * Settable only while it is absent. Moving a reference from one contact to another is a real
     * event in a CRM and is refused here rather than applied, because silently allowing it would let
     * one push redirect another representative's exchange to a different person. An operator resolves
     * it deliberately; `contact.reconcile_refused` records when the two systems began disagreeing.
     *
     * Not settable on create, and that is deliberate rather than an omission: a locally created
     * contact having no reference is what lets a later `reconcile` link them to a CRM record instead
     * of having to choose between two claims about who they are.
     */
    externalRef: zod_1.z.string().min(1).max(200).optional(),
})
    .strict();
/**
 * The CRM's whole view of a person, pushed by the host's back end before it opens the widget (DG.36).
 *
 * All three fields are required, unlike a patch, because this is not a change to our record: it is the
 * other system's statement of who this person is, applied to ours. In particular the address cannot be
 * optional. It is the notification recipient and, under D-25, the sign-in credential, so a push without
 * one has nothing to create a contact from.
 *
 * Required is not the same as authoritative, and the address is the field where the two part company. A
 * push may choose one for somebody who does not exist here yet, and a push carrying a *different*
 * address for somebody who does is refused with a 409 rather than applied, because that address is how
 * they sign in. The display name has no such rule and the CRM simply wins.
 *
 * The reference travels here in the body and not as a path segment, which is the one thing about this
 * endpoint worth defending in advance. It is host-controlled text of up to 200 characters, and a CRM
 * record id containing a slash - `ACC/2026/0231` is an ordinary form - would split into two segments and
 * route somewhere else entirely. Percent-encoding moves the correctness of every push into whichever
 * HTTP client the host happens to use, and a proxy that normalises `%2F` on the way through breaks it
 * with no error anybody sees. A body has exactly one reading.
 */
exports.reconcileContactRequestSchema = zod_1.z
    .object({
    /** Bounded at 200 to match `contact.external_ref` and `embedTokenRequestSchema`'s own reference field. */
    externalRef: zod_1.z.string().min(1).max(200),
    email: zod_1.z.string().min(3).max(254),
    displayName: zod_1.z.string().min(1).max(200),
})
    .strict();
/**
 * Which of the four things a push did.
 *
 * Published rather than left for a host to infer, because a directory that mirrors a CRM is written to
 * on almost every widget open and a host needs to be able to tell the writes apart from the noise:
 * `unchanged` is what nearly every push answers and the one thing not worth logging. A refusal is not
 * an arm here - it is a 409 with the colliding contact id, because it carries no contact of its own to
 * describe.
 */
exports.reconcileActionSchema = zod_1.z.enum(['created', 'linked', 'updated', 'unchanged']);
exports.reconcileContactResponseSchema = zod_1.z.object({
    action: exports.reconcileActionSchema,
    contact: exports.contactSchema,
    /**
     * Which of our fields the CRM's values overwrote. Present on `updated`, absent on the other three.
     *
     * Absent rather than an empty array, because empty would be a claim rather than a silence: a `linked`
     * does change the reference and possibly the display name, and the link is audited without reporting
     * which fields those were. An empty list there would say nothing happened.
     */
    changedFields: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.contactListSchema = zod_1.z.object({ contacts: zod_1.z.array(exports.contactSchema) });
// ---------------------------------------------------------------------------
// Representative generic upload link
// ---------------------------------------------------------------------------
exports.uploadLinkStatusSchema = zod_1.z.enum(['active', 'revoked']);
exports.uploadLinkSchema = zod_1.z.object({
    id: uuid,
    status: exports.uploadLinkStatusSchema,
    url: zod_1.z.url(),
    createdAt: zod_1.z.iso.datetime(),
    revokedAt: zod_1.z.iso.datetime().nullable(),
});
exports.createUploadLinkRequestSchema = zod_1.z.strictObject({});
exports.uploadLinkResponseSchema = zod_1.z.object({
    link: exports.uploadLinkSchema.nullable(),
    created: zod_1.z.boolean().optional(),
});
exports.genericLinkUploadItemSchema = zod_1.z.object({
    id: uuid,
    genericUploadLinkId: uuid,
    guestName: zod_1.z.string(),
    guestEmail: zod_1.z.string(),
    documentId: uuid,
    documentName: zod_1.z.string(),
    byteLength: zod_1.z.number().optional(),
    contentType: zod_1.z.string().optional(),
    createdAt: zod_1.z.iso.datetime(),
});
exports.genericLinkUploadsResponseSchema = zod_1.z.object({
    uploads: zod_1.z.array(exports.genericLinkUploadItemSchema),
});
exports.associateGenericUploadsRequestSchema = zod_1.z.strictObject({
    uploadIds: zod_1.z.array(uuid).min(1).max(100),
    contactId: uuid,
});
exports.associateGenericUploadsResponseSchema = zod_1.z.object({
    exchangeId: uuid,
    associated: zod_1.z.number().int().min(1),
});
// ---------------------------------------------------------------------------
// Exchanges
// ---------------------------------------------------------------------------
exports.exchangeStateSchema = zod_1.z.enum(['active', 'archived']);
exports.exchangeSchema = zod_1.z.object({
    id: uuid,
    contactId: uuid,
    custodianId: uuid,
    title: zod_1.z.string(),
    state: exports.exchangeStateSchema,
});
exports.createExchangeRequestSchema = zod_1.z
    .object({
    contactId: uuid,
    title: zod_1.z.string().min(1).max(200),
})
    .strict();
exports.updateExchangeRequestSchema = zod_1.z
    .object({
    /** The only mutable field here. Archiving is its own endpoint, and custody is neither. */
    title: zod_1.z.string().min(1).max(200),
})
    .strict();
exports.exchangeListSchema = zod_1.z.object({ exchanges: zod_1.z.array(exports.exchangeSchema) });
// ---------------------------------------------------------------------------
// The request checklist
// ---------------------------------------------------------------------------
exports.requestStateSchema = zod_1.z.enum(['awaiting', 'fulfilled', 'withdrawn']);
exports.documentRequestSchema = zod_1.z.object({
    id: uuid,
    exchangeId: uuid,
    label: zod_1.z.string(),
    state: exports.requestStateSchema,
    fulfilledBy: uuid.nullable(),
    requestedAt: zod_1.z.iso.datetime(),
});
exports.createDocumentRequestSchema = zod_1.z
    .object({
    label: zod_1.z.string().min(1).max(10_000),
})
    .strict();
exports.documentRequestListSchema = zod_1.z.object({ requests: zod_1.z.array(exports.documentRequestSchema) });
// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
exports.documentVersionSchema = zod_1.z.object({
    id: uuid,
    ordinal: zod_1.z.number().int().positive(),
    byteLength: zod_1.z.number().int().nonnegative(),
    contentType: zod_1.z.string(),
    sha256: zod_1.z.string().length(64),
    scanStatus: zod_1.z.string(),
    committedAt: zod_1.z.iso.datetime(),
});
exports.documentSchema = zod_1.z.object({
    id: uuid,
    exchangeId: uuid,
    name: zod_1.z.string(),
    /** Which side sent it. A surface shows this; section 11 decides with it. */
    origin: zod_1.z.enum(['rep', 'contact']),
    currentVersionId: uuid.nullable(),
    /**
     * When it appeared in the exchange.
     *
     * Added in stage 7. Figure 4's document table has an "Updated" column and figure 5 dates
     * every entry, and neither was servable: the domain record dropped the column. `document`
     * carries `created_at` and no `updated_at`, so this is when the first version committed
     * rather than the newest - which is why the name says "added" and not "updated".
     */
    addedAt: zod_1.z.iso.datetime(),
    removedAt: zod_1.z.iso.datetime().nullable(),
});
exports.documentListSchema = zod_1.z.object({ documents: zod_1.z.array(exports.documentSchema) });
exports.documentDetailSchema = zod_1.z.object({
    document: exports.documentSchema,
    versions: zod_1.z.array(exports.documentVersionSchema),
});
/**
 * What `GET /documents/:id/content` answers a caller that sent `Accept: application/json`, when the
 * repository can delegate the read. The surface navigates to `url`; it must never fetch it.
 *
 * A scripted cross-origin caller cannot follow the 302 this replaced. The redirect crosses into a third
 * origin, which taints the request's origin to `null` for the second hop, so the repository is asked with
 * an origin no CORS configuration can name. A navigation is not CORS-checked, so it works against a
 * repository configured for nothing - which is also why this does not depend on what D-17's Content
 * Manager turns out to support.
 *
 * `expiresAt` is section 13's five-minute life, published so a surface can say why a stale tab's download
 * failed rather than showing the repository's own error page.
 */
exports.documentContentUrlSchema = zod_1.z.object({
    url: zod_1.z.url(),
    expiresAt: zod_1.z.iso.datetime(),
});
// ---------------------------------------------------------------------------
// The activity projection
// ---------------------------------------------------------------------------
/**
 * One audit event, as a surface may see it.
 *
 * The hash and the previous hash are deliberately absent. They are what makes the chain
 * verifiable and they belong to `audit:verify` and to a records export, not to a timeline
 * a rep scrolls: a surface that displayed them would invite somebody to check them by eye
 * and conclude the wrong thing.
 */
exports.activityEventSchema = zod_1.z.object({
    seq: zod_1.z.number().int().positive(),
    occurredAt: zod_1.z.string(),
    actorKind: zod_1.z.enum(['rep', 'guest', 'service', 'admin']),
    actorId: uuid,
    action: zod_1.z.string(),
    subjectKind: zod_1.z.string(),
    subjectId: zod_1.z.string(),
});
/**
 * The same event with the two identifiers resolved to names, which is what `/activity` answers.
 *
 * A separate schema rather than two optional fields on the one above, and the asymmetry is the whole
 * point (DG.23). A records export leaves the building, so it keeps identifiers; the custodian reading
 * their own exchange can already see every filename in the document list beside this trail, so
 * resolving them there discloses nothing new. Two schemas make that a property of the types rather
 * than of somebody remembering which argument to pass, and `recordsExportSchema` below still reuses
 * the unresolved one.
 *
 * `nullable` rather than `optional`: a name that could not be resolved is an explicit null, so a
 * backend that stopped resolving them fails a surface's own response validation instead of quietly
 * rendering blanks. Plenty of subjects have no name to resolve - a session and a challenge are
 * credentials - and null is the honest answer for those too.
 */
exports.resolvedActivityEventSchema = exports.activityEventSchema.extend({
    actorName: zod_1.z.string().nullable(),
    subjectName: zod_1.z.string().nullable(),
});
/**
 * The custodian is on the envelope rather than on each event, because it is one fact about the
 * exchange and not about any event in it.
 *
 * Since DG.35 the first event on a chain is `guest.grant_issued` with the *service* as its actor:
 * nobody pressed anything, the dispatcher minted the grant on the strength of a decision the rep had
 * already made. So the trail has to say the send was automatic and still let a reviewer find whose
 * instruction caused it, and putting that name in the actor position would file a decision against a
 * person who did not make it in that transaction. It goes here instead.
 */
exports.activitySchema = zod_1.z.object({
    events: zod_1.z.array(exports.resolvedActivityEventSchema),
    custodianName: zod_1.z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Records access (S6.7)
// ---------------------------------------------------------------------------
exports.recordsAccessRequestSchema = zod_1.z
    .object({
    /** Mandatory: section 11 audits every records access with a stated reason. */
    reason: zod_1.z.string().min(1).max(500),
})
    .strict();
exports.recordsExportSchema = zod_1.z.object({
    generatedAt: zod_1.z.iso.datetime(),
    reason: zod_1.z.string(),
    exchange: exports.exchangeSchema,
    contact: exports.contactSchema.nullable(),
    requests: zod_1.z.array(exports.documentRequestSchema),
    documents: zod_1.z.array(exports.documentDetailSchema),
    activity: zod_1.z.array(exports.activityEventSchema),
    removedDocumentCount: zod_1.z.number().int().nonnegative(),
    downloads: zod_1.z.array(zod_1.z.object({
        documentId: uuid,
        versionId: uuid,
        url: zod_1.z.url(),
        expiresAt: zod_1.z.iso.datetime(),
    })),
});
//# sourceMappingURL=api.js.map